import React from "react"

import { TextField as MuiTextField } from "@mui/material"
import { styled } from "@mui/material/styles"

import { Logic } from "@cirrus/fe-lib"

import { Adornments } from "../../../lib/_private"

const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const getInputPadding = ({ label, size, typography, variant }) => {
  if (!label || variant === "outlined") {
    return {
      paddingBottom: size === "small" ? typography.pxToRem(9) : typography.pxToRem(11),
      paddingTop: size === "small" ? typography.pxToRem(8) : typography.pxToRem(11),
    }
  }

  return {
    paddingBottom: size === "small" ? typography.pxToRem(2) : typography.pxToRem(2),
    paddingTop: size === "small" ? typography.pxToRem(18) : typography.pxToRem(20),
  }
}

const StyledTextField = styled(MuiTextField)(
  ({
    background,
    backgroundOnHover,
    disableUnderline,
    error,
    hiddenLabel,
    label,
    multiline,
    multipleValues,
    size,
    theme: { typography, withPalette, palette },
    variant,
    width,
  }) => {
    const height = {
      [multipleValues ? "minHeight" : "height"]:
        size === "small" ? typography.pxToRem(38) : typography.pxToRem(44),
      ...(multipleValues && !hiddenLabel ? { minHeight: typography.pxToRem(52) } : {}),
    }

    if (multiline) {
      delete height.height
    }

    return {
      [`& .Mui${capitalize(variant)}Input-root`]: {
        ["& textarea.MuiInputBase-input"]: {
          paddingRight: multiline ? "12px" : "0",
        },
        [`& .Mui${capitalize(variant)}Input-input`]: {
          fontSize: size === "small" ? typography.pxToRem(12) : typography.pxToRem(15),
          height: size === "small" ? typography.pxToRem(18) : typography.pxToRem(22),
          ...getInputPadding({ label, size, typography, variant }),
        },
        "&.Mui-disabled": {
          backgroundColor: withPalette(background),
        },
        "&.Mui-focused": {
          backgroundColor: withPalette(backgroundOnHover),
        },
        "&:after": {
          borderColor: error ? "#cf4444" : palette.primary.main,
        },
        "&:hover": {
          backgroundColor: withPalette(backgroundOnHover),
        },
        backgroundColor: withPalette(background),
        borderRadius: disableUnderline && "4px",
        paddingBottom: 0,
        paddingRight: multiline ? "0" : "12px",
        paddingTop: 0,
        width,

        ...height,
      },
      "& .MuiFormHelperText-root": {
        marginLeft: 0,
      },
      "& .MuiAutocomplete-tag": {
        marginTop: Logic.consequence(true, hiddenLabel, undefined, typography.pxToRem(20)),
      },
      "& .MuiInputLabel-root": {
        "&:not([class*='MuiFormLabel-shrink'])": {
          marginTop: "-0.4rem",
        },
        "&:not([class*='MuiInputLabel-shrink'])": {
          marginTop: multipleValues && !hiddenLabel ? "-6px" : "-0.4rem",
        },
      },
      width,
    }
  },
)

export const TextField2 = ({
  background = "#f1f3f4",
  backgroundOnHover,
  disableUnderline = false,
  endAdornment,
  error,
  fullWidth,
  helperText,
  hiddenLabel,
  inputRootClass,
  InputProps = {},
  inputProps = {},
  label,
  multiline,
  multipleValues,
  params = { InputProps: { className: null } },
  placeholder,
  required,
  rows,
  size = "medium",
  startAdornment,
  variant = "filled",
  width = 320,
  ...props
}) => {
  const normalizedVariant = variant === "legacy" ? "outlined" : variant

  let renderPlaceholder = placeholder || ""

  if (renderPlaceholder && required && (hiddenLabel || !label)) {
    renderPlaceholder = `${renderPlaceholder}*`
  }

  return (
    <StyledTextField
      data-qa={props.id}
      multipleValues={multipleValues}
      multiline={multiline}
      label={hiddenLabel ? null : label}
      hiddenLabel={label ? hiddenLabel : true}
      placeholder={renderPlaceholder}
      required={required}
      rows={rows}
      error={error}
      helperText={helperText}
      fullWidth={fullWidth}
      InputProps={{
        ...params.InputProps,
        ...InputProps,
        ...Adornments.connected({
          endAdornment,
          startAdornment,
        }),
        className: inputRootClass,
        disableUnderline,
      }}
      inputProps={{ ...params.inputProps, ...inputProps }}
      {...{
        background,
        backgroundOnHover: backgroundOnHover || background,
        disableUnderline,
        size,
        variant: normalizedVariant,
        width: fullWidth ? "100%" : width,
      }}
      {...props}
    />
  )
}
